import { AxiosInstance } from 'axios';
import React, { useEffect, useState } from 'react';
import { getAuthTokens } from 'utils/cookies';

let isRefreshing = false;
let refreshingCall: Promise<any>;

type SuccessArg = { token: string; refreshToken: string; expires: string };
interface Props {
  onRefresh: () => Promise<{
    token: string;
    refreshToken: string;
    expires: string;
  }>;
  onSuccess: (p: SuccessArg) => void;
  onError: (e: Error) => void;
  instances: AxiosInstance[];
}

export const AxiosInterceptorsRefreshToken: React.FC<Props> = ({
  children,
  onRefresh,
  onSuccess,
  onError,
  instances,
}) => {
  const [canRender, setCanRender] = useState(false);
  useEffect(() => {
    setCanRender(true);

    const result = instances.map((instance) => {
      const id = instance.interceptors.response.use(
        (response) => response,
        (error) => {
          const status = error.response ? error.response.status : null;

          const { token, refreshToken } = getAuthTokens();

          if (status !== 401 || !(token && refreshToken)) {
            return Promise.reject(error);
          }

          if (isRefreshing) {
            return refreshingCall.then(() => {
              return instance(error.config);
            });
          }
          isRefreshing = true;
          refreshingCall = onRefresh()
            .then((res) => {
              isRefreshing = false;
              const { token, refreshToken, expires } = res;
              onSuccess({ token, refreshToken, expires });
              return instance(error.config);
            })
            .catch((err) => {
              const errorStatus = err.response ? err.response.status : null;
              isRefreshing = false;
              if (errorStatus === 401) {
                onError(err);
              }
              return Promise.reject(err);
            });
          return refreshingCall;
        },
      );
      return { id, instance };
    });

    return () => {
      result.forEach(({ id, instance }) => {
        instance.interceptors.response.eject(id);
      });
    };
  }, [onRefresh, onSuccess, onError, instances]);

  return <>{canRender ? children : null}</>;
};
